import {
  conditionEpisodeEnvelopeSchema,
  conditionEpisodeListEnvelopeSchema,
  createConditionEpisodeRequestSchema,
  createExamManeuverRequestSchema,
  createGlobalSurgicalPeriodRequestSchema,
  createImplantUdiRecordRequestSchema,
  createPatientReportedOutcomeRequestSchema,
  examManeuverEnvelopeSchema,
  examManeuverListEnvelopeSchema,
  globalPeriodModifierEnvelopeSchema,
  globalSurgicalPeriodEnvelopeSchema,
  globalSurgicalPeriodListEnvelopeSchema,
  implantUdiRecordEnvelopeSchema,
  implantUdiRecordListEnvelopeSchema,
  patientReportedOutcomeEnvelopeSchema,
  patientReportedOutcomeListEnvelopeSchema,
  type ConditionEpisodeResponse,
  type CreateConditionEpisodeRequest,
  type CreateExamManeuverRequest,
  type CreateGlobalSurgicalPeriodRequest,
  type CreateImplantUdiRecordRequest,
  type CreatePatientReportedOutcomeRequest,
  type ExamManeuverResponse,
  type GlobalPeriodModifierQuery,
  type GlobalSurgicalPeriodResponse,
  type ImplantUdiRecordResponse,
  type PatientReportedOutcomeResponse,
} from '@emr/shared';

import { apiRequest } from './client.ts';

/**
 * The orthopedics specialty-module API surface (`BACKLOG.md` M7), validated against `@emr/shared`'s
 * own request/response schemas — the exact same schemas `server/src/orthopedics/routes.ts` validates
 * request bodies with, matching `api/dermatology.ts`/`api/aesthetics.ts`'s own stated reasoning for why
 * this cannot silently drift from what the server actually accepts and returns.
 */

export type {
  ConditionEpisodeResponse,
  CreateConditionEpisodeRequest,
  CreateExamManeuverRequest,
  CreateGlobalSurgicalPeriodRequest,
  CreateImplantUdiRecordRequest,
  CreatePatientReportedOutcomeRequest,
  ExamManeuverResponse,
  GlobalSurgicalPeriodResponse,
  ImplantUdiRecordResponse,
  PatientReportedOutcomeResponse,
};

function ortho(patientId: string, suffix = ''): string {
  return `/api/patients/${patientId}/orthopedics${suffix}`;
}

// --- Condition episodes -----------------------------------------------------------------------------

export async function fetchConditionEpisodes(
  patientId: string,
  signal?: AbortSignal,
): Promise<readonly ConditionEpisodeResponse[]> {
  const response = await apiRequest(
    ortho(patientId, '/condition-episodes'),
    conditionEpisodeListEnvelopeSchema,
    signal === undefined ? {} : { signal },
  );
  return response.conditionEpisodes;
}

export async function createConditionEpisode(
  patientId: string,
  input: CreateConditionEpisodeRequest,
): Promise<ConditionEpisodeResponse> {
  createConditionEpisodeRequestSchema.parse(input);
  const response = await apiRequest(
    ortho(patientId, '/condition-episodes'),
    conditionEpisodeEnvelopeSchema,
    { method: 'POST', body: input },
  );
  return response.conditionEpisode;
}

// --- Exam maneuvers ----------------------------------------------------------------------------------

export async function fetchExamManeuvers(
  patientId: string,
  signal?: AbortSignal,
): Promise<readonly ExamManeuverResponse[]> {
  const response = await apiRequest(
    ortho(patientId, '/exam-maneuvers'),
    examManeuverListEnvelopeSchema,
    signal === undefined ? {} : { signal },
  );
  return response.examManeuvers;
}

export async function createExamManeuver(
  patientId: string,
  input: CreateExamManeuverRequest,
): Promise<ExamManeuverResponse> {
  createExamManeuverRequestSchema.parse(input);
  const response = await apiRequest(
    ortho(patientId, '/exam-maneuvers'),
    examManeuverEnvelopeSchema,
    {
      method: 'POST',
      body: input,
    },
  );
  return response.examManeuver;
}

// --- Patient-reported outcomes -----------------------------------------------------------------------

export async function fetchPatientReportedOutcomes(
  patientId: string,
  signal?: AbortSignal,
): Promise<readonly PatientReportedOutcomeResponse[]> {
  const response = await apiRequest(
    ortho(patientId, '/patient-reported-outcomes'),
    patientReportedOutcomeListEnvelopeSchema,
    signal === undefined ? {} : { signal },
  );
  return response.patientReportedOutcomes;
}

export async function createPatientReportedOutcome(
  patientId: string,
  input: CreatePatientReportedOutcomeRequest,
): Promise<PatientReportedOutcomeResponse> {
  createPatientReportedOutcomeRequestSchema.parse(input);
  const response = await apiRequest(
    ortho(patientId, '/patient-reported-outcomes'),
    patientReportedOutcomeEnvelopeSchema,
    { method: 'POST', body: input },
  );
  return response.patientReportedOutcome;
}

// --- Global surgical periods -------------------------------------------------------------------------

export async function fetchGlobalSurgicalPeriods(
  patientId: string,
  signal?: AbortSignal,
): Promise<readonly GlobalSurgicalPeriodResponse[]> {
  const response = await apiRequest(
    ortho(patientId, '/global-surgical-periods'),
    globalSurgicalPeriodListEnvelopeSchema,
    signal === undefined ? {} : { signal },
  );
  return response.globalSurgicalPeriods;
}

export async function createGlobalSurgicalPeriod(
  patientId: string,
  input: CreateGlobalSurgicalPeriodRequest,
): Promise<GlobalSurgicalPeriodResponse> {
  createGlobalSurgicalPeriodRequestSchema.parse(input);
  const response = await apiRequest(
    ortho(patientId, '/global-surgical-periods'),
    globalSurgicalPeriodEnvelopeSchema,
    { method: 'POST', body: input },
  );
  return response.globalSurgicalPeriod;
}

/** The modifier a claim needs, recomputed by the server every call — never cached, per
 * `@emr/shared`'s own header on why the modifier is never stored. */
export async function fetchGlobalPeriodModifier(
  patientId: string,
  periodId: string,
  query: GlobalPeriodModifierQuery,
): Promise<{ readonly ok: boolean; readonly modifier: string | null; readonly reason: string }> {
  const params = new URLSearchParams({
    serviceDate: query.serviceDate,
    relationship: query.relationship,
  });
  const response = await apiRequest(
    ortho(patientId, `/global-surgical-periods/${periodId}/modifier?${params.toString()}`),
    globalPeriodModifierEnvelopeSchema,
  );
  return response.result;
}

// --- Implant UDI records ---------------------------------------------------------------------------

export async function fetchImplantUdiRecords(
  patientId: string,
  signal?: AbortSignal,
): Promise<readonly ImplantUdiRecordResponse[]> {
  const response = await apiRequest(
    ortho(patientId, '/implant-udi-records'),
    implantUdiRecordListEnvelopeSchema,
    signal === undefined ? {} : { signal },
  );
  return response.implantUdiRecords;
}

export async function createImplantUdiRecord(
  patientId: string,
  input: CreateImplantUdiRecordRequest,
): Promise<ImplantUdiRecordResponse> {
  createImplantUdiRecordRequestSchema.parse(input);
  const response = await apiRequest(
    ortho(patientId, '/implant-udi-records'),
    implantUdiRecordEnvelopeSchema,
    { method: 'POST', body: input },
  );
  return response.implantUdiRecord;
}
